"use client";

import { useCallback } from "react";

import { useProjectFilterStore } from "@/store/useProjectFilterStore";
import type { DeadlineFilterStatus, ProjectStatus } from "@/types";

import type { FilterScopeDraftFields } from "./useFilterScopeCascade";
import { useFilterScopeCascade } from "./useFilterScopeCascade";

const mapScopePatchToProjectDraft = (fields: Partial<FilterScopeDraftFields>) => ({
  ...(fields.selectedProject !== undefined
    ? { selectedAssignedProject: fields.selectedProject }
    : {}),
  ...(fields.selectedProjectLabel !== undefined
    ? { selectedAssignedProjectLabel: fields.selectedProjectLabel }
    : {}),
  ...(fields.selectedSite !== undefined ? { selectedSite: fields.selectedSite } : {}),
  ...(fields.selectedSiteLabel !== undefined
    ? { selectedSiteLabel: fields.selectedSiteLabel }
    : {}),
  ...(fields.selectedAssets !== undefined ? { selectedAssets: fields.selectedAssets } : {}),
  ...(fields.selectedAssetLabels !== undefined
    ? { selectedAssetLabels: fields.selectedAssetLabels }
    : {}),
  ...(fields.selectedStandard !== undefined ? { selectedStandard: fields.selectedStandard } : {}),
});

interface UseProjectFiltersOptions {
  role: string;
}

export function useProjectFilters({ role }: UseProjectFiltersOptions) {
  const { draft, setDraft, applyFilters, reset, applied, syncDraftFromApplied } =
    useProjectFilterStore();

  const hasAppliedFilters = Object.values(applied).some((value) =>
    Array.isArray(value) ? value.length > 0 : !!value
  );

  const hasDraftFilters =
    !!draft.selectedStandard ||
    !!draft.selectedAssignedProject ||
    !!draft.selectedSite ||
    draft.selectedAssets.length > 0 ||
    draft.projectStatusFilters.length > 0 ||
    draft.deadlineFilters.length > 0;

  const setDraftFromScope = useCallback(
    (fields: Partial<FilterScopeDraftFields>) => setDraft(mapScopePatchToProjectDraft(fields)),
    [setDraft]
  );

  const cascade = useFilterScopeCascade({
    role,
    draft: {
      selectedProject: draft.selectedAssignedProject,
      selectedProjectLabel: draft.selectedAssignedProjectLabel,
      selectedSite: draft.selectedSite,
      selectedSiteLabel: draft.selectedSiteLabel,
      selectedAssets: draft.selectedAssets,
      selectedAssetLabels: draft.selectedAssetLabels,
      selectedStandard: draft.selectedStandard,
    },
    setDraft: setDraftFromScope,
  });

  const handleStandardChange = useCallback(
    (value: string) => setDraft({ selectedStandard: value }),
    [setDraft]
  );

  const handleToggleDeadline = useCallback(
    (option: DeadlineFilterStatus) => {
      const next = draft.deadlineFilters.includes(option)
        ? draft.deadlineFilters.filter((current) => current !== option)
        : [...draft.deadlineFilters, option];
      setDraft({ deadlineFilters: next });
    },
    [draft.deadlineFilters, setDraft]
  );

  const handleToggleStatus = useCallback(
    (option: ProjectStatus) => {
      const next = draft.projectStatusFilters.includes(option)
        ? draft.projectStatusFilters.filter((current) => current !== option)
        : [...draft.projectStatusFilters, option];
      setDraft({ projectStatusFilters: next });
    },
    [draft.projectStatusFilters, setDraft]
  );

  return {
    draft,
    hasAppliedFilters,
    hasDraftFilters,
    reset,
    applyFilters,
    syncDraftFromApplied,
    handleStandardChange,
    handleToggleDeadline,
    handleToggleStatus,
    ...cascade,
  };
}
